import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { GET_TRUST_CENTER_ADMIN_USERS } from "../../../../apis/trust-center-apis/trustCenterApis";
import {
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import {
  DialogHeader,
  dialogActionsSx,
  dialogPaperProps,
} from "../../components/DialogParts";

// Picks which admins get an email for each new access request. Only the
// customer's admins can be chosen (the server checks it again).
const RequestAlertsDialog = ({ open, emails, onSave, onClose }) => {
  const theme = useTheme();
  const [chosen, setChosen] = useState(emails);
  const [saving, setSaving] = useState(false);
  const { data, loading, error } = useQuery(GET_TRUST_CENTER_ADMIN_USERS, {
    skip: !open,
    fetchPolicy: "cache-and-network",
  });
  const admins = (data?.getTrustCenterAdminUsers || []).map((a) => a.email);

  useEffect(() => {
    if (open) {
      setChosen(emails);
      setSaving(false);
    }
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    setSaving(true);
    const ok = await onSave(chosen);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={dialogPaperProps}
    >
      <DialogHeader
        title="Email alerts for access requests"
        subtitle="These admins get an email each time a visitor asks for a document."
        onClose={() => !saving && onClose()}
      />
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>
            Could not load your admins. Close this and try again.
          </Alert>
        )}
        <Autocomplete
          multiple
          filterSelectedOptions
          options={admins}
          value={chosen}
          loading={loading}
          onChange={(_, value) => setChosen(value)}
          noOptionsText={
            loading ? "Loading admins…" : "No other admins to add."
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Admins to email"
              placeholder={chosen.length ? "" : "Choose admins"}
              helperText="Only people with the Admin role are listed. Leave it empty to send no emails."
            />
          )}
        />
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1.5,
            color: theme.customColors.text.secondary,
          }}
        >
          The email shows the visitor's name, email, company, reason and the
          document, with a link to accept or decline it here.
        </Typography>
      </DialogContent>
      <DialogActions sx={dialogActionsSx(theme)}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          sx={outlinedButtonSx(theme)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving}
          sx={primaryButtonSx(theme)}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Above the access requests list: who is emailed about new requests.
const RequestAlertsCard = ({ emails = [], onSave }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  return (
    <Paper
      elevation={0}
      aria-label="Email alerts"
      component="section"
      sx={{
        px: 2.5,
        py: 1.75,
        mb: 2,
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.main}`,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <MailOutlineIcon
            sx={{ color: theme.customColors.icons.primary, mt: 0.25 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }}>Email alerts</Typography>
            {emails.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: theme.customColors.text.secondary }}
              >
                Nobody is emailed about new requests yet. Choose admins to get
                an email each time a visitor asks for a document.
              </Typography>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{ color: theme.customColors.text.secondary, mb: 0.75 }}
                >
                  These admins get an email for each new request:
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  sx={{ flexWrap: "wrap", rowGap: 0.75 }}
                >
                  {emails.map((email) => (
                    <Chip
                      key={email}
                      size="small"
                      label={email}
                      sx={{
                        backgroundColor: theme.customColors.background.light,
                      }}
                    />
                  ))}
                </Stack>
              </>
            )}
          </Box>
        </Stack>
        <Button
          variant="outlined"
          onClick={() => setOpen(true)}
          sx={{ ...outlinedButtonSx(theme), flexShrink: 0 }}
        >
          {emails.length ? "Change admins" : "Choose admins"}
        </Button>
      </Stack>
      <RequestAlertsDialog
        open={open}
        emails={emails}
        onSave={onSave}
        onClose={() => setOpen(false)}
      />
    </Paper>
  );
};

export default RequestAlertsCard;
